import React, { useEffect, useState } from 'react';
import { 
    MessageSquare, Clock, CheckCircle2, AlertCircle, 
    ChevronDown, ChevronUp, Send, Loader2, User, Mail,
    Calendar, Filter, RefreshCcw
} from 'lucide-react';
import { fetchAllInquiries, updateInquiryStatus, InquiryDB } from '../../../services/inquiryService';
import { useToast } from '../../../context/ToastContext';
import { useLanguage } from '../../../context/LanguageContext';
import { at } from '../../../i18nAdmin';

export const InquiriesTab: React.FC = () => {
    const { lang } = useLanguage();
    const { showToast } = useToast();
    const [inquiries, setInquiries] = useState<InquiryDB[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [answerText, setAnswerText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [filterStatus, setFilterStatus] = useState<string>('all');

    const loadInquiries = async () => {
        setIsLoading(true);
        try {
            const data = await fetchAllInquiries();
            setInquiries(data);
        } catch (error) {
            console.error(error);
            showToast('error', at(lang, 'loadInquiriesFailed'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadInquiries();
    }, []);

    const handleUpdateStatus = async (inquiryId: string, status: string, answer?: string) => {
        setIsSubmitting(true);
        try {
            await updateInquiryStatus(inquiryId, status, answer);
            showToast('success', at(lang, 'inquiryStatusUpdated'));
            setAnswerText('');
            loadInquiries();
        } catch (error) {
            console.error(error);
            showToast('error', at(lang, 'updateFailed'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const filteredInquiries = inquiries.filter(inq => {
        if (filterStatus === 'all') return true;
        return inq.status === filterStatus;
    });

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'pending':
                return <span className="px-2 py-1 bg-amber-50 text-amber-600 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-1"><Clock size={10} /> {at(lang, 'pending')}</span>;
            case 'answered':
                return <span className="px-2 py-1 bg-emerald-50 text-emerald-600 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-1"><CheckCircle2 size={10} /> {at(lang, 'answered')}</span>;
            case 'closed':
                return <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded-lg text-[10px] font-black uppercase tracking-wider flex items-center gap-1"><AlertCircle size={10} /> {at(lang, 'closed')}</span>;
            default:
                return null;
        }
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleString('ko-KR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl font-black text-slate-900 tracking-tight uppercase">{at(lang, 'customerInquiries')}</h2>
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">{at(lang, 'manageInquiries')}</p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                        <select 
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-600 focus:outline-none focus:ring-2 focus:ring-rose-500/10 appearance-none"
                        >
                            <option value="all">{at(lang, 'allStatus')}</option>
                            <option value="pending">{at(lang, 'pending')}</option>
                            <option value="answered">{at(lang, 'answered')}</option>
                            <option value="closed">{at(lang, 'closed')}</option>
                        </select>
                    </div>
                    <button 
                        onClick={loadInquiries}
                        className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 md:hover:text-rose-500 md:hover:border-rose-200 transition-all"
                    >
                        <RefreshCcw size={18} className={isLoading ? 'animate-spin' : ''} />
                    </button>
                </div>
            </div>

            {isLoading ? (
                <div className="py-20 flex justify-center">
                    <Loader2 className="animate-spin text-rose-500" size={40} />
                </div>
            ) : filteredInquiries.length === 0 ? (
                <div className="py-20 text-center bg-white rounded-[32px] border border-slate-100">
                    <MessageSquare size={48} className="mx-auto text-slate-100 mb-4" />
                    <p className="text-slate-400 font-bold text-sm uppercase tracking-widest">{at(lang, 'noInquiries')}</p>
                </div>
            ) : (
                <div className="grid gap-4">
                    {filteredInquiries.map((inquiry) => (
                        <div 
                            key={inquiry.id}
                            className={`bg-white rounded-[28px] border-2 transition-all duration-300 ${
                                expandedId === inquiry.id ? 'border-rose-500 shadow-xl shadow-rose-50' : 'border-slate-50 md:hover:border-slate-100'
                            }`}
                        >
                            <div 
                                onClick={() => {
                                    setExpandedId(expandedId === inquiry.id ? null : inquiry.id);
                                    setAnswerText(inquiry.answer || '');
                                }}
                                className="p-6 cursor-pointer"
                            >
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-2">
                                            {getStatusBadge(inquiry.status)}
                                            <span className="px-2 py-1 bg-slate-100 text-slate-500 rounded-lg text-[10px] font-black uppercase tracking-wider">
                                                {inquiry.category || at(lang, 'general')}
                                            </span>
                                        </div>
                                        <h4 className="text-lg font-black text-slate-900 truncate uppercase tracking-tight">
                                            {inquiry.title}
                                        </h4>
                                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                                            <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                                <User size={12} /> {inquiry.user?.name || at(lang, 'unknown')}
                                            </div>
                                            <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                                <Mail size={12} /> {inquiry.user?.email || 'N/A'}
                                            </div>
                                            <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                                <Calendar size={12} /> {formatDate(inquiry.created_at)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-slate-300 hidden md:block">
                                        {expandedId === inquiry.id ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                                    </div>
                                </div>
                            </div>

                            {expandedId === inquiry.id && (
                                <div className="px-6 pb-6 space-y-6 animate-in slide-in-from-top-2">
                                    <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100">
                                        <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3">{at(lang, 'userInquiry')}</h5>
                                        <p className="text-slate-700 text-sm font-medium leading-relaxed whitespace-pre-wrap">
                                            {inquiry.content}
                                        </p>
                                    </div>

                                    <div className="space-y-3">
                                        <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">{at(lang, 'adminResponse')}</h5>
                                        <textarea 
                                            value={answerText}
                                            onChange={(e) => setAnswerText(e.target.value)}
                                            placeholder={at(lang, 'writeResponse')}
                                            rows={4}
                                            className="w-full bg-slate-50 border-2 border-slate-50 rounded-2xl px-5 py-4 text-sm font-bold text-slate-900 focus:outline-none focus:border-rose-500/20 focus:bg-white transition-all resize-none"
                                        />
                                        <div className="flex justify-end gap-2">
                                            <button 
                                                onClick={() => handleUpdateStatus(inquiry.id, 'closed')}
                                                disabled={isSubmitting}
                                                className="px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest text-slate-400 md:hover:text-slate-900 transition-colors"
                                            >
                                                {at(lang, 'closeWithoutAnswer')}
                                            </button>
                                            <button 
                                                onClick={() => handleUpdateStatus(inquiry.id, 'answered', answerText)}
                                                disabled={isSubmitting || !answerText.trim()}
                                                className="bg-slate-900 text-white px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest shadow-lg md:hover:bg-rose-500 active:scale-95 disabled:opacity-50 transition-all flex items-center gap-2"
                                            >
                                                {isSubmitting ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} /> {at(lang, 'sendAnswer')}</>}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
